import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Card,
  Loader,
  Tab,
  Grid,
  Header,
  Segment,
  Button,
  Container,
  Modal,
} from "semantic-ui-react";
import { useQuery, useMutation } from "@apollo/client";
import { GET_APPLICATION_BY_REFERENCE_ID } from "../../../services/graphql/queries/EmployerQueries";
import { UPDATE_APPLICATION_STATUS_BY_EMPLOYER_REFERENCE_ID_MUTATION } from "../../../services/graphql/mutations/EmployerMutations";
import ApplicantOutreachModal from "./ApplicantOutreachModal";
import {
  getFailureToastWithMessage,
  getSuccessToastWithMessage,
} from "../../shared/toast/ToastOptions";
import PdfView from "./PdfView";
import { fetchFile } from "../../../services/employer/EmployerServices";

const ApplicationView = (props) => {
  const employerReferenceId = props.match.params.employer_reference_id;
  const [resume, setResume] = useState(null);
  const [coverLetter, setCoverLetter] = useState(null);

  const { loading, error, data } = useQuery(GET_APPLICATION_BY_REFERENCE_ID, {
    variables: {
      employerReferenceId: employerReferenceId,
    },
  });

  useEffect(() => {
    if (data !== undefined) {
      if (data.applicationsByEmployerReferenceId[0].resumeS3Bucket !== null) {
        fetchFile(
          data.applicationsByEmployerReferenceId[0].resumeS3Bucket,
          data.applicationsByEmployerReferenceId[0].resumeS3Key
        ).then(async (response) => {
          const reader = response.body.getReader();
          const { done, value } = await reader.read();
          setResume({ data: value });
        });
      }
      if (
        data.applicationsByEmployerReferenceId[0].coverLetterS3Bucket !== null
      ) {
        fetchFile(
          data.applicationsByEmployerReferenceId[0].coverLetterS3Bucket,
          data.applicationsByEmployerReferenceId[0].coverLetterS3Key
        ).then(async (response) => {
          const reader = response.body.getReader();
          const { done, value } = await reader.read();
          setCoverLetter({ data: value });
        });
      }
    }
  }, [data]);

  const [updateApplicationStatus, { mutationData }] = useMutation(
    UPDATE_APPLICATION_STATUS_BY_EMPLOYER_REFERENCE_ID_MUTATION
  );

  const [isRejectApplicantModalOpen, setIsRejectApplicantModalOpen] = useState(
    false
  );

  const [
    isApplicantOutReachModalOpen,
    setIsApplicantOutReachModalOpen,
  ] = useState(false);

  const handleRejectApplicant = () => {
    updateApplicationStatus({
      variables: {
        employerReferenceId: employerReferenceId,
        newStatus: "rejected",
      },
    })
      .then(() => {
        props.openToast(
          getSuccessToastWithMessage("Successfully rejected applicant.")
        );
      })
      .catch((error) => {
        console.log(error);
        props.openToast(
          getFailureToastWithMessage("Failed to reject applicant.")
        );
      });
    setIsRejectApplicantModalOpen(false);
  };

  const getJobPostingHeader = () => {
    return (
      data.applicationsByEmployerReferenceId[0].jobPosting.role +
      (data.applicationsByEmployerReferenceId[0].jobPosting.team !== null
        ? " | "
        : "") +
      data.applicationsByEmployerReferenceId[0].jobPosting.team
    );
  };

  const formattedJobLocation = () => {
    return `${data.applicationsByEmployerReferenceId[0].jobPosting.location.city}, 
            ${data.applicationsByEmployerReferenceId[0].jobPosting.location.state}, 
            ${data.applicationsByEmployerReferenceId[0].jobPosting.location.zipCode}`;
  };

  const applicantInfo = () => {
    return (
      <Segment>
        {data.applicationsByEmployerReferenceId[0].applicantInfo.firstName !=
          null && (
          <Header as={"h2"}>
            {`${data.applicationsByEmployerReferenceId[0].applicantInfo.firstName} ${data.applicationsByEmployerReferenceId[0].applicantInfo.lastName}`}
          </Header>
        )}
        <Header as={"h3"}>
          {data.applicationsByEmployerReferenceId[0].applicantInfo.emailAddress}
        </Header>
        <Header as={"h5"}>
          Application Date:{" "}
          {data.applicationsByEmployerReferenceId[0].dateApplied}
        </Header>
        <Header as={"h5"}>
          Status: {data.applicationsByEmployerReferenceId[0].status}
        </Header>
      </Segment>
    );
  };

  const generate_panes = () => [
    data.applicationsByEmployerReferenceId[0].resumeS3Bucket && {
      menuItem: "Resume",
      render: () => (
        <Tab.Pane>
          <PdfView file={resume} />
        </Tab.Pane>
      ),
    },
    data.applicationsByEmployerReferenceId[0].coverLetterS3Bucket && {
      menuItem: "Cover Letter",
      render: () => (
        <Tab.Pane>
          <PdfView file={coverLetter} />
        </Tab.Pane>
      ),
    },
    {
      menuItem: "Parsed Applicant Info",
      render: () => <Tab.Pane>Tab kyle Content</Tab.Pane>,
    },
  ];

  if (error) {
    return <Card>Error!</Card>;
  } else if (loading) {
    return <Loader active />;
  } else {
    return (
      <Container fluid>
        <Segment.Group horizontal>
          {applicantInfo()}
          <Segment>
            <Grid columns={2}>
              <Grid.Column>
                <Header as={"h2"}>{getJobPostingHeader()}</Header>
                <Header as={"h3"}>{formattedJobLocation()}</Header>
              </Grid.Column>
              <Grid.Column>
                <Container fluid textAlign="center">
                  <Button.Group attached="bottom">
                    <Button
                      compact
                      color="green"
                      onClick={() => {
                        setIsApplicantOutReachModalOpen(true);
                      }}
                    >
                      Email Applicant
                    </Button>
                    <Button.Or />
                    <Button
                      compact
                      color="red"
                      onClick={() => {
                        setIsRejectApplicantModalOpen(true);
                      }}
                    >
                      Reject Applicant
                    </Button>
                  </Button.Group>
                </Container>
              </Grid.Column>
            </Grid>
          </Segment>
        </Segment.Group>
        <Tab panes={generate_panes()} />
        <Modal
          open={isRejectApplicantModalOpen}
          onOpen={() => {
            setIsRejectApplicantModalOpen(true);
          }}
          onClose={() => {
            setIsRejectApplicantModalOpen(false);
          }}
        >
          <Modal.Header>Reject Applicant?</Modal.Header>
          <Modal.Content>{applicantInfo()}</Modal.Content>
          <Modal.Actions>
            <Container textAlign="center">
              <Button.Group attached="bottom">
                <Button positive onClick={handleRejectApplicant}>
                  Confirm
                </Button>
                <Button.Or />
                <Button
                  negative
                  onClick={() => {
                    setIsRejectApplicantModalOpen(false);
                  }}
                >
                  Cancel
                </Button>
              </Button.Group>
            </Container>
          </Modal.Actions>
        </Modal>
        <ApplicantOutreachModal
          applicantEmailAddress={
            data.applicationsByEmployerReferenceId[0].applicantInfo.emailAddress
          }
          employerReferenceId={employerReferenceId}
          isApplicantOutReachModalOpen={isApplicantOutReachModalOpen}
          setIsApplicantOutReachModalOpen={setIsApplicantOutReachModalOpen}
        />
      </Container>
    );
  }
};

const mapDispatchToProps = (dispatch) => {
  return {
    openToast: (toast) => dispatch({ type: "OPEN_TOAST", payload: toast }),
  };
};

export default connect(null, mapDispatchToProps)(ApplicationView);
